import React from 'react';
import { Code, Shield, Brain, Cloud, Cog, Zap, ArrowRight, BuildingIcon, MonitorSmartphoneIcon } from 'lucide-react';
import { apiService } from '../services/api';
import useApi from '../hooks/useApi';
import LoadingSpinner, { SkeletonCard } from './LoadingSpinner';
import { ErrorMessage } from './ErrorBoundary';

const Services = () => {
  const { data: services, loading, error, refetch } = useApi(() => apiService.getServices(), []);

  const iconMap = {
    Code: Code,
    Shield: Shield,
    Brain: Brain,
    Cloud: Cloud,
    Cog: Cog,
    Zap: Zap,
    Build: MonitorSmartphoneIcon,
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (error) {
    return (
      <section id="services" className="section-padding bg-gray-50">
        <div className="container mx-auto px-6 lg:px-8">
          <ErrorMessage message={error} onRetry={refetch} />
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="section-padding bg-gray-50">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="h-1 w-12 bg-[#2aa996]"></div>
            <span className="text-[#2aa996] font-semibold uppercase tracking-wider text-sm">
              Our Services
            </span>
            <div className="h-1 w-12 bg-[#2aa996]"></div>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            AI-First <span className="gradient-text">Services & Solutions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From AI strategy and consulting to custom AI development, software engineering, and digital transformation— 
            we deliver services that help you see the future and build the capabilities to get there first.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            // Loading skeletons
            [...Array(6)].map((_, index) => (
              <SkeletonCard key={index} />
            ))
          ) : (
            services?.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Code;
              return (
                <div
                  key={service.id}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#2aa996] to-[#1e7a6b] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="text-white" size={28} />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-gray-800">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      {service.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-[#2aa996] rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-[#2aa996] font-bold text-lg">
                      {service.pricing}
                    </span>
                    <button 
                      onClick={scrollToContact}
                      className="flex items-center space-x-2 text-[#2aa996] hover:text-[#1e7a6b] transition-colors duration-300 group-hover:translate-x-1"
                    >
                      <span className="font-medium">Get Quote</span>
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="text-center mt-16">
          <button 
            onClick={scrollToContact}
            className="btn-primary text-lg px-8 py-4"
          >
            Discuss Your Project
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
